import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaUser, FaStore, FaTrash } from "react-icons/fa";
import "./EmployeeList.css";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [stores, setStores] = useState({}); // To cache store details by storeId
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState(null); // For popup
  const [showModal, setShowModal] = useState(false); // Modal visibility state
  const [selectedPercentages, setSelectedPercentages] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); 


  const handleDeleteUser = async (employeeId) => {
    try {
      await axios.delete(`https://api.swasthmitra.com/api/users/delete-user/${employeeId}`);
      setEmployees(prevEmployees => prevEmployees.filter(employee => employee._id !== employeeId));
      setDeleteConfirm(null); // Reset delete confirmation
      alert("Employee deleted successfully!");
    } catch (err) {
      console.error("Error deleting employee:", err);
      setError("Error deleting employee");
    }
  };

  // Fetch store data by ID and cache it
  const fetchStoreById = async (storeId) => {
    if (stores[storeId]) {
      return stores[storeId]; // Return from cache if already fetched
    }
    try {
      const response = await axios.get(
        `https://api.swasthmitra.com/api/stores/fetchstores/${storeId}`
      );
      setStores((prevStores) => ({
        ...prevStores,
        [storeId]: { ...response.data.store }, // Cache store details
      }));
      return response.data.store;
    } catch (err) {
      console.error(`Error fetching store with ID ${storeId}:`, err);
      if (err.response && err.response.status === 404) {
        alert(`Store with ID ${storeId} not found!`);
      }
      setError("Error fetching store data");
      return null;
    }
  };

  const handlePercentageChange = (employeeId, percentage) => {
    setSelectedPercentages((prev) => ({
      ...prev,
      [employeeId]: percentage,
    }));
  };

  const handleUpdatePercentage = async (employeeId) => {
    const percentage = selectedPercentages[employeeId];
    
    if (percentage === undefined || percentage === null) {
      alert("Please select a percentage value");
      return;
    }
  
    try {
      console.log('Sending update request:', { employeeId, percentage }); // Debug log
      
      const response = await axios.put(
        `https://api.swasthmitra.com/api/users/update-token-percentage/${employeeId}`,
        { percentage: percentage }
      );
      
      console.log('Update response:', response.data); // Debug log
      
      // Update local state
      setEmployees(prevEmployees => 
        prevEmployees.map(employee => 
          employee._id === employeeId 
            ? { ...employee, tokenPercentage: percentage } 
            : employee
        )
      );
      
      alert("Percentage updated successfully!");
    } catch (err) {
      console.error("Error updating percentage:", err.response?.data || err.message);
      alert(`Failed to update percentage: ${err.response?.data?.message || err.message}`);
    }
  };

  // Handle status change (Activate or Deactivate)
  const handleStatusChange = async (userId, currentStatus) => {
    const newStatus = currentStatus === "approved" ? "pending" : "approved";
    try {
      const response = await axios.put(
        `https://api.swasthmitra.com/api/users/update-status/${userId}`
      );
      const updatedUser = response.data.user;
      setEmployees((prevEmployees) =>
        prevEmployees.map((employee) =>
          employee._id === updatedUser.id
            ? { ...employee, status: updatedUser.status }
            : employee
        )
      );
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Error updating status");
    }
  };
  

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
  };

  useEffect(() => {
    if (statusFilter === "all") {
      setFilteredEmployees(employees);
    } else {
      setFilteredEmployees(
        employees.filter((employee) => employee.status === statusFilter)
      );
    }
  }, [employees, statusFilter]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(
          "https://api.swasthmitra.com/api/users/employees"
        );
        const sortedEmployees = response.data.employees.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        ); // Sort by creation date (newest first)
        setEmployees(sortedEmployees);
      } catch (err) {
        setError("Error fetching employees");
        console.error("Error fetching employees:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Handle modal open and fetch store details only when needed
  const handleOpenModal = async (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
    const data = await fetchStoreById(employee.storeId); // Fetch store details when opening modal
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmployee(null);
  };

  // Verify user action
  const handleVerifyUser = async () => {
    if (selectedEmployee) {
      await handleStatusChange(selectedEmployee._id, selectedEmployee.status);
      handleCloseModal();
    }
  };

  return (
    <div className="employee-list">
      <h2 className="title">Swasth Mitra List</h2>

      {loading && <p className="loading">Loading...</p>}
      {error && <p>{error}</p>}

      <div className="filter-container">
        <label htmlFor="status-filter">Filter by status:</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={handleFilterChange}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
        </select>
      </div>

      {filteredEmployees.length === 0 && !loading && <p>No employees found</p>}

      {filteredEmployees.length > 0 && (
        <table className="employee-table">
          <thead>
            <tr>
              <th>Name</th>
              {/* <th>Email</th> */}
              <th>Status</th>
              <th>Token %</th>
              <th>Set-Percentage</th>
              <th>Action</th>
              <th>Delete</th> {/* New column for delete */}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((employee) => (
              <tr key={employee._id}>
                <td onClick={() => handleOpenModal(employee)}>
                  {employee.name}
                </td>

                {/* <td>{employee.email.length > 5
                    ? `${employee.email.slice(0, 10)}...`
                    : employee.email} </td> */}
                <td>
                <button
  className="status-btn"
  onClick={() => handleStatusChange(employee._id, employee.status)}
>
  {employee.status === "approved" ? "Deactivate" : "Activate"}
</button>
                </td>
                <td>
                  <div className="token-percentage">
                    {employee.tokenPercentage || "N/A"}%
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    value={selectedPercentages[employee._id] || ""}
                    onChange={(e) =>
                      handlePercentageChange(employee._id, e.target.value)
                    }
                    min="0"
                    max="100"
                    placeholder="Enter %"
                    className="percentage-input"
                  />
                </td>
                <td>
                  <button
                    className="update-btn"
                    onClick={() => handleUpdatePercentage(employee._id)}
                  >
                    Update
                  </button>
                </td>
                <td>
                  {deleteConfirm === employee._id ? (
                    <div className="delete-confirmation">
                      <button 
                        className="confirm-delete-btn"
                        onClick={() => handleDeleteUser(employee._id)}
                      >
                        Confirm
                      </button>
                      <button 
                        className="cancel-delete-btn"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="delete-btn"
                      onClick={() => setDeleteConfirm(employee._id)}
                      title="Delete Employee"
                    >
                      <FaTrash />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && selectedEmployee && (
        <div className="modal">
          <div className="modal-content">
            <button className="close-btn" onClick={handleCloseModal}>
              &times;
            </button>

            <h3>
              <FaUser style={{ marginRight: "10px", color: "#007bff" }} />{" "}
              Employee Details
            </h3>
            <div className="details-section">
              <p>
                <strong>Name:</strong> {selectedEmployee.name}
              </p>
              <p>
                <strong>Mobile:</strong> {selectedEmployee.MobileNumber}
              </p>
              <p>
                <strong>Email:</strong> {selectedEmployee.email}
              </p>
              <p>
                <strong>Role:</strong> {selectedEmployee.role}
              </p>
              <p>
                <strong>Status:</strong> {selectedEmployee.status}
              </p>
            </div>

            <h3>
              <FaStore style={{ marginRight: "10px", color: "#28a745" }} />{" "}
              Store Details
            </h3>
            <div className="details-section">
              {selectedEmployee.storeId && stores[selectedEmployee.storeId] ? (
                <>
                  <p>
                    <strong>Store Name:</strong>{" "}
                    {stores[selectedEmployee.storeId].name}
                  </p>
                  <p>
                    <strong>Address:</strong>{" "}
                    {stores[selectedEmployee.storeId].address}
                  </p>
                  <p>
                    <strong>Contact.No:</strong>{" "}
                    {stores[selectedEmployee.storeId].mobile}
                  </p>
                  <p>
                    <strong>No Of Employees:</strong>{" "}
                    {stores[selectedEmployee.storeId].employees?.length}
                  </p>
                </>
              ) : (
                <p>No Store Assigned</p>
              )}
            </div>

            <div>
              {selectedEmployee.status === "pending" && (
                <button className="verify-btn" onClick={handleVerifyUser}>
                  Verify User
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;
